/**
 * ARAM Platform - Auth Store
 * Core Desarrollos S.A.S - Copyright 2024-2025
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/lib/api'

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  phone?: string
  avatar_url?: string
  tenant_id: number
  is_active: boolean
  is_superuser: boolean
  roles: Array<{ id: number; name: string; code: string }>
  permissions: string[]
  // Vertical architecture
  vertical?: string
  enabled_modules?: string[]
  vertical_info?: {
    name: string
    description: string
    icon: string
    color: string
    tagline: string
    modules: string[]
  }
  // Per-user overrides
  home_path?: string | null
  menu_permissions?: string[] | null  // null = use vertical defaults; ["ALL"] = unrestricted
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // MFA state
  requiresMfa: boolean
  mfaSetupRequired: boolean  // NEW: User needs to setup MFA
  mfaTempToken: string | null
  mfaEmail: string | null
  
  // Actions
  login: (email: string, password: string, mfaCode?: string) => Promise<void>
  completeSso: (accessToken: string, refreshToken: string, user: User) => void
  verifyMfa: (mfaCode: string) => Promise<void>
  setupMfa: () => Promise<{ secret: string; qr_code: string; backup_codes: string[] }>
  enableMfa: (code1: string, code2: string, backupConfirmed: boolean) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  fetchCurrentUser: () => Promise<void>
  clearError: () => void
  clearMfaState: () => void
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // MFA state
      requiresMfa: false,
      mfaSetupRequired: false,
      mfaTempToken: null,
      mfaEmail: null,
      
      login: async (email: string, password: string, mfaCode?: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await apiClient.post('/auth/login', {
            email,
            password,
            mfa_code: mfaCode,
          })
          
          const data = response.data
          
          // Check if MFA setup is required (new user or admin without MFA)
          if (data.mfa_setup_required) {
            set({
              mfaSetupRequired: true,
              mfaTempToken: data.access_token || data.temp_token,
              mfaEmail: email,
              isLoading: false,
            })
            return
          }
          
          // Check if MFA verification is required
          if (data.requires_mfa) {
            set({
              requiresMfa: true,
              mfaTempToken: data.temp_token,
              mfaEmail: email,
              isLoading: false,
            })
            return
          }
          
          const { access_token, refresh_token, user } = data
          
          // Set auth header for future requests
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
          
          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            requiresMfa: false,
            mfaSetupRequired: false,
            mfaTempToken: null,
            mfaEmail: null,
          })
        } catch (error: any) {
          const message = error.response?.data?.detail || 'Error al iniciar sesión'
          set({ error: message, isLoading: false })
          throw new Error(message)
        }
      },

      completeSso: (accessToken: string, refreshToken: string, user: User) => {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        set({ user, accessToken, refreshToken, isAuthenticated: true, isLoading: false, error: null, requiresMfa: false, mfaSetupRequired: false, mfaTempToken: null, mfaEmail: null })
      },
      
      setupMfa: async () => {
        const { mfaEmail, mfaTempToken } = get()
        
        if (!mfaEmail || !mfaTempToken) {
          throw new Error('Sesión expirada')
        }
        
        const response = await apiClient.post('/auth/mfa/setup', {
          email: mfaEmail,
          temp_token: mfaTempToken,
        })
        
        return response.data
      },
      
      enableMfa: async (code1: string, code2: string, backupConfirmed: boolean) => {
        const { mfaTempToken, mfaEmail } = get()
        
        if (!mfaTempToken || !mfaEmail) {
          throw new Error('Sesión expirada')
        }
        
        set({ isLoading: true, error: null })
        
        try {
          await apiClient.post('/auth/mfa/enable', {
            email: mfaEmail,
            mfa_code_1: code1,
            mfa_code_2: code2,
            temp_token: mfaTempToken,
            backup_codes_confirmed: backupConfirmed,
          })
          
          // MFA enabled, now complete login
          set({
            mfaSetupRequired: false,
            isLoading: false,
          })
          
          // Re-login to get proper token
          // The user will be redirected to login again with MFA
          
        } catch (error: any) {
          const message = error.response?.data?.detail || 'Error al activar MFA'
          set({ error: message, isLoading: false })
          throw new Error(message)
        }
      },
      
      verifyMfa: async (mfaCode: string) => {
        const { mfaTempToken, mfaEmail } = get()
        
        if (!mfaTempToken || !mfaEmail) {
          set({ error: 'Sesión MFA expirada', isLoading: false })
          return
        }
        
        set({ isLoading: true, error: null })
        
        try {
          const response = await apiClient.post('/auth/mfa/verify', {
            email: mfaEmail,
            temp_token: mfaTempToken,
            mfa_code: mfaCode,
          })
          
          const { access_token, refresh_token, user } = response.data
          
          // Set auth header for future requests
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
          
          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            requiresMfa: false,
            mfaTempToken: null,
            mfaEmail: null,
          })
        } catch (error: any) {
          const message = error.response?.data?.detail || 'Código MFA inválido'
          set({ error: message, isLoading: false })
          throw new Error(message)
        }
      },
      
      logout: () => {
        void apiClient.post('/auth/logout').catch(() => undefined)
        // Clear auth header
        delete apiClient.defaults.headers.common['Authorization']
        
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },
      
      refreshAccessToken: async () => {
        const { refreshToken } = get()
        
        if (!refreshToken) {
          get().logout()
          throw new Error('No refresh token available')
        }
        
        // Use apiClient directly — the interceptor in api.ts guards against
        // re-entering this path when the refresh endpoint itself returns 401.
        const response = await apiClient.post('/auth/refresh', {
          refresh_token: refreshToken,
        })
        
        const { access_token, refresh_token } = response.data
        
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
        
        set({
          accessToken: access_token,
          refreshToken: refresh_token,
        })
      },
      
      fetchCurrentUser: async () => {
        set({ isLoading: true })
        
        try {
          const response = await apiClient.get('/auth/me')
          set({ user: response.data, isLoading: false })
        } catch {
          set({ isLoading: false })
          get().logout()
        }
      },
      
      clearError: () => set({ error: null }),
      
      clearMfaState: () => set({ 
        requiresMfa: false, 
        mfaSetupRequired: false,
        mfaTempToken: null, 
        mfaEmail: null,
      }),
      
      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false
        if (user.is_superuser) return true
        return user.permissions?.includes(permission) ?? false
      },
      
      hasAnyPermission: (permissions: string[]) => {
        const { user } = get()
        if (!user) return false
        if (user.is_superuser) return true
        return permissions.some(p => user.permissions?.includes(p))
      },
    }),
    {
      name: 'aram-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Initialize auth header on app start
const { accessToken } = useAuthStore.getState()
if (accessToken) {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
}
